import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from "react";
import { AuthContextType, AuthUser } from "./types";
import {
    clearStreamEditorToken,
    EditorAuthError,
    fetchStreamEditorWhoami,
    getStreamEditorToken,
    installStreamEditorAuth,
    redeemEditorCode,
    setStreamEditorToken,
    startSiteLogin,
    takeEditorParamsFromUrl,
} from "../../api/streamEditorAuth";

// Токен редактора (вход через сайт) уходит заголовком во все запросы редактора эфира — с первого же запроса.
installStreamEditorAuth();

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Hardcoded credentials
const VALID_CREDENTIALS = [
    { username: "user", password: "Torsunov2025", role: "user" as const },
    { username: "admin", password: "TorsunovAdmin2025", role: "admin" as const },
];

const SITE_ERRORS: Record<string, string> = {
    not_translator: "Этот адрес не в списке переводчиков эфира — войдите прежним логином редактора.",
    failed: "Вход через сайт не удался — попробуйте ещё раз.",
};

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
    const [user, setUser] = useState<AuthUser | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [siteLoginError, setSiteLoginError] = useState<string | null>(null);

    // Вход через сайт: код из адреса → токен редактора → кто это (translate-python → site_server). Только
    // переводчик эфира из списка входит так; остальным — сообщение и прежний вход логином.
    const signInWithEditorToken = useCallback(async (): Promise<AuthUser | null> => {
        try {
            const who = await fetchStreamEditorWhoami();
            if (!who.translator || !who.new_process) {
                clearStreamEditorToken();
                setSiteLoginError(SITE_ERRORS.not_translator);
                return null;
            }
            const siteUser: AuthUser = {
                username: who.email,
                role: who.role === "admin" ? "admin" : "user",
                site: true,
                email: who.email,
                features: who.features || [],
            };
            return siteUser;
        } catch (err) {
            if (err instanceof EditorAuthError && (err.status === 401 || err.status === 403)) {
                clearStreamEditorToken();
                setSiteLoginError(err.message);
            } else {
                setSiteLoginError(`Вход через сайт не проверен: ${(err as Error).message}`);
            }
            return null;
        }
    }, []);

    useEffect(() => {
        let alive = true;
        (async () => {
            const { code, error } = takeEditorParamsFromUrl();
            if (error) setSiteLoginError(SITE_ERRORS[error] || SITE_ERRORS.failed);
            if (code) {
                try {
                    const got = await redeemEditorCode(code);
                    setStreamEditorToken(got.token, got.email);
                } catch (err) {
                    setSiteLoginError((err as Error).message);
                }
            }
            if (getStreamEditorToken()) {
                const siteUser = await signInWithEditorToken();
                if (!alive) return;
                if (siteUser) {
                    setUser(siteUser);
                    setIsAuthenticated(true);
                    setLoading(false);
                    return;
                }
            }
            const storedUser = localStorage.getItem("torsunov_user");
            if (storedUser) {
                try {
                    const userData = JSON.parse(storedUser);
                    setUser(userData);
                    setIsAuthenticated(true);
                } catch (error) {
                    console.error("Error parsing stored user data:", error);
                    localStorage.removeItem("torsunov_user");
                }
            }
            if (alive) setLoading(false);
        })();
        return () => {
            alive = false;
        };
    }, [signInWithEditorToken]);

    const login = (username: string, password: string): boolean => {
        const match = VALID_CREDENTIALS.find(
            (cred) => cred.username === username && cred.password === password,
        );
        if (match) {
            const userData = { username, role: match.role };
            setUser(userData);
            setIsAuthenticated(true);
            localStorage.setItem("torsunov_user", JSON.stringify(userData));
            return true;
        }
        return false;
    };

    const loginWithSite = (returnTo: string) => {
        startSiteLogin(returnTo);
    };

    const logout = () => {
        setUser(null);
        setIsAuthenticated(false);
        localStorage.removeItem("torsunov_user");
        clearStreamEditorToken();
    };

    return (
        <AuthContext.Provider
            value={{ isAuthenticated, user, login, loginWithSite, logout, loading, siteLoginError }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};

/** Новые возможности редактора эфира — только переводчику из списка, вошедшему через сайт. */
export const useStreamEditorFeature = (feature: string): boolean => {
    const context = useContext(AuthContext);
    return Boolean(context?.user?.site && context.user.features?.includes(feature));
};
